import React from 'react';
import {
    Typography,
    Card,
    Divider,
} from '@mui/material';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import autoRefreshedExecuteHasAllClientOrgLicenseStatusWithToken from '../tools/permissions/hasAllClientOrgLicenseStatus';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import autoRefreshedExecuteGetFetchExamResultWithToken from '../tools/getFetchExamResult';
import autoRefreshedExecuteFetchUUDsWithToken from '../tools/getFetchUUIDs';
import { ExamUUIDObject } from '../tools/getFetchUUIDs';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import NativeSelect from '@mui/material/NativeSelect';
import { getFetchExamResultResponse } from '../tools/getFetchExamResult';
import { BarChart, Bar, Rectangle, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadialBarChart, RadialBar, AreaChart, Area } from 'recharts';
import autoRefreshedExecuteGetFetchExamResultPerUuidWithToken from '../tools/getFetchExamResultPerUUID';
import { ResultItem, ResultDetail } from '../tools/getFetchExamResultPerUUID';
import autoRefreshedExecuteHasAllClientOrgRolesWithToken from '../tools/permissions/hasAllClientOrgRoles';
import { useLanguage } from '../../contexts/LanguageContext';
import { LANG_CODE } from "../views/AppFooter";
import { QUIZ_DATA } from '../../consts';
import OnsoAnalysis from './OnsoAnalysis';
import handleFetchRecent5ExamResult from '../tools/handleFetchRecent5ExamResult';

interface GameCardProps {
    path: string;
}

interface examResultScore {
    [key: string]: number;
}

type examResultScores = (examResultScore | null)[];

type ExamObjectList = ExamUUIDObject[];

interface DataType {
    subject: string;
    A: number;
    fullMark: number;
}

type ConstructedData = {
    uuid: string;
    datetime: string;
    eachResult: ResultDetail;
    score: number;
};

function formatDatetime(datetime: string): string {
    const date = new Date(datetime);
    const year = date.getFullYear().toString().slice(2); // Get last two digits of the year
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, '0'); // Day of the month
    return `${year}-${month}-${day}`;
}

function constructData(resultItems: ResultItem[] | []): ConstructedData[] {
    return resultItems.map(([uuid, datetime, eachResult, score]) => ({
        uuid,
        datetime: formatDatetime(datetime),
        eachResult,
        score,
    }));
}


const convertToPercentage = (data: getFetchExamResultResponse): examResultScores => {
    const transformedResults = data.results.map((result) => {
        // Handle null result
        if (!result) return null;

        // Calculate percentage for each key in the examResult object
        const score: examResultScore = {};
        for (const key in result) {
            const values = result[key];
            // Ensure array has values
            if (values && values.length > 0) {
                const trueCount = values.filter(value => value).length;
                const percentage = (trueCount / values.length) * 100;
                score[key] = percentage;
            } else {
                score[key] = 0; // Default to 0% if no values
            }
        }
        return score;
    });

    return transformedResults;
};



const convertToPercentage2 = (data: getFetchExamResultResponse): examResultScores => {
    const mergedResults: { [key: string]: boolean[] } = {};

    // Merge all results
    data.results.forEach((result) => {
        if (result) {
            for (const key in result) {
                // If the key already exists in mergedResults, concatenate the arrays
                if (mergedResults[key]) {
                    mergedResults[key] = mergedResults[key].concat(result[key]);
                } else {
                    // Otherwise, initialize it with the current array
                    mergedResults[key] = result[key];
                }
            }
        }
    });

    // Convert merged results to percentages
    const transformedResults: examResultScores = [];
    for (const key in mergedResults) {
        const values = mergedResults[key];
        const trueCount = values.filter(value => value).length;
        const percentage = (trueCount / values.length) * 100;
        transformedResults.push({ [key]: percentage });
    }

    return transformedResults;
};


const getWorst7Subjects = (response: getFetchExamResultResponse): { key: string; percentage: number }[] => {
    // Merge and convert the response to percentage
    const percentages = convertToPercentage2(response);

    // Flatten the results into a single list of {key, percentage}
    const allSubjects: { key: string; percentage: number }[] = [];
    percentages.forEach((result) => {
        if (result) {
            for (const key in result) {
                allSubjects.push({ key, percentage: result[key] });
            }
        }
    });

    // Sort subjects by percentage (ascending order)
    const sortedSubjects = allSubjects.sort((a, b) => a.percentage - b.percentage);

    // Get the worst 10 subjects
    const worst10Subjects = sortedSubjects.slice(0, 7);

    return worst10Subjects;
};


function extractUUIDs(examUUIDs: ExamUUIDObject[]): string[] {
    return examUUIDs.map(item => item.uuid);
};

const transformDataForChart = (subjects: { key: string; percentage: number }[]): { name: string, score: number, fill: string }[] => {
    // Define the color palette for the chart bars
    // const colorPalette = [
    //     '#8884d8',
    //     '#83a6ed',
    //     '#8dd1e1',
    //     '#82ca9d',
    //     '#a4de6c',
    //     '#d0ed57',
    //     '#ffc658',
    //     '#ff8042',
    //     '#ffbb28',
    //     '#ff665e',
    // ];

    // Map through the worst 10 subjects and transform them into the desired format
    return subjects.map((subject, index) => ({
        name: subject.key,              // Use the subject key as the name
        score: subject.percentage,      // The percentage is the score
        fill: '#4dabf5',
        // fill: colorPalette[index] || '#8884d8',  // Assign a color from the palette, fallback to a default color
    }));
};

function calculateAverageScore(data: DataType[]): number {
    if (data.length === 0) {
        return 0; // Return 0 for an empty list
    }
    const totalScore = data.reduce((sum, item) => sum + item.A, 0);
    const averageScore = totalScore / data.length
    return parseFloat(averageScore.toFixed(2));
}


const ExamResult = () => {

    const [hasPermission, setHasPermission] = React.useState<boolean | null>(null);
    const navigate = useNavigate()
    const accessToken = localStorage.getItem('access_token');
    const [hasViewPermission, setHasViewPermission] = React.useState<boolean | null>(null);
    const { language } = useLanguage();

    // React.useEffect(() => {
    //     localStorage.setItem('currentExamUuid', '')
    //     localStorage.setItem('currentExamResult', '{}')
    //     // localStorage.removeItem('currentExamUuid');
    //     // localStorage.removeItem('currentExamResult');
    // }, []);

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await autoRefreshedExecuteHasAllClientOrgLicenseStatusWithToken(accessToken, "project", "dummyKey", ["professional"]);
                setHasPermission(response.has_all_rights); // Update permission state
            } catch (error) {
                console.error("Error fetching data:", error);
                setHasPermission(false); // Default to no permission on error
            }
        };

        fetchData();
    }, [accessToken]);

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await autoRefreshedExecuteHasAllClientOrgRolesWithToken(accessToken, "project", "dummyKey", ["view"]);
                setHasViewPermission(response.has_all_rights); // Update permission state
            } catch (error) {
                console.error("Error fetching data:", error);
                setHasViewPermission(false); // Default to no permission on error
            }
        };

        fetchData();
    }, [accessToken]);

    const [allAvailableUUIDs, setAllAvailableUUIDs] = React.useState<ExamObjectList | null>(null)

    React.useEffect(() => {
        const fetchUUIDs = async () => {
            try {
                const response = await autoRefreshedExecuteFetchUUDsWithToken(accessToken);
                setAllAvailableUUIDs(response)
                setSelectedDateUuidPair({ uuid: response[0].uuid, end_at: response[0].end_at });
                console.log("Fetched UUIDs:", response);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchUUIDs();
    }, [accessToken]);

    const [selectedDateUuidPair, setSelectedDateUuidPair] = React.useState<{ uuid: string; end_at: string } | null>(null);
    const [selectedUuidResult, setSelectedUuidResult] = React.useState<examResultScores>([]);
    const [constructedData, setConstructedData] = React.useState<DataType[]>([]);
    const [worstSubjectsChartData, setworstSubjectsChartData] = React.useState<{ name: string, score: number, fill: string }[]>([]);
    const [histricalData, setHistricalData] = React.useState<ConstructedData[] | []>([]);
    const [onsoScoreData, setOnsoScoreData] = React.useState<{ [onso: string]: number }>({});


    React.useEffect(() => {
        const handleFetchResultFromUUID = async () => {
            try {
                if (selectedDateUuidPair?.uuid !== null) {
                    const targetUuid: string = selectedDateUuidPair?.uuid!;
                    console.log('first');
                    console.log(targetUuid);

                    // Fetch the data
                    const response = await autoRefreshedExecuteGetFetchExamResultWithToken(accessToken, [targetUuid]);
                    console.log('second');
                    console.log(response.results);

                    // Convert and update selectedUuidResult
                    const convertedResults = convertToPercentage({ results: response.results });
                    setSelectedUuidResult(convertedResults);

                    // Derive constructedData directly
                    const data = convertedResults
                        .filter((result): result is examResultScore => result !== null)
                        .flatMap(result => {
                            return Object.entries(result).map(([key, value]) => ({
                                subject: key,
                                A: value,
                                fullMark: 100,
                            }));
                        });

                    setConstructedData(data);
                    console.log('third');
                    console.log(data);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        handleFetchResultFromUUID();
    }, [selectedDateUuidPair]);

    React.useEffect(() => {
        const handleFetchRecentNExamResult = async () => {
            try {
                const response = await autoRefreshedExecuteGetFetchExamResultWithToken(accessToken, extractUUIDs(allAvailableUUIDs ?? []));
                const worstSubjects = getWorst7Subjects(response);
                const worstSubjectsChartData = transformDataForChart(worstSubjects);
                setworstSubjectsChartData(worstSubjectsChartData)
                console.log("worstSubjects")
                console.log(worstSubjectsChartData);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        handleFetchRecentNExamResult();
    }, [allAvailableUUIDs]);

    React.useEffect(() => {
        handleFetchRecent5ExamResult(allAvailableUUIDs, setOnsoScoreData, accessToken);
    }, [allAvailableUUIDs]);

    const handleDateSelection = (event: React.ChangeEvent<{ value: unknown }>) => {
        const selectedEndAt = event.target.value as string;

        // Find the selected item based on the end_at value
        const selectedItem = allAvailableUUIDs?.find(item => item.end_at === selectedEndAt);

        // Update state with both uuid and end_at
        if (selectedItem) {
            setSelectedDateUuidPair({ uuid: selectedItem.uuid, end_at: selectedItem.end_at });
        }
    }

    React.useEffect(() => {
        const handleFetchNCreateHistricalTrend = async () => {
            try {
                const response = await autoRefreshedExecuteGetFetchExamResultPerUuidWithToken(accessToken, extractUUIDs(allAvailableUUIDs ?? []));
                console.log(constructData(response.results))
                setHistricalData(constructData(response.results))
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        handleFetchNCreateHistricalTrend();
    }, [allAvailableUUIDs]);

    // Handle loading state
    if (hasPermission === null || hasViewPermission === null) {
        return (
            <Box sx={{ mt: 3, mb: 7, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                <Card elevation={0} sx={{ maxWidth: 1000, height: '780px' }} style={{ margin: '0px', padding: '0px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }} >
                        <Typography variant="h5" color="text.primary" sx={{ mt: 0, fontSize: '24px' }}>
                            Loading...
                        </Typography>
                    </Box>
                </Card>
            </Box>
        )
    }

    // Go to signin page if no permission
    if (!hasPermission) {
        navigate("/sign-in/");
        window.scrollTo(0, 0);
        return null;
        // return <NotFoundPage />;
    }
    if (!hasViewPermission) {
        navigate("/sign-in/");
        window.scrollTo(0, 0);
        return null;
        // return <NotFoundPage />;
    }

    const formatDate = (isoDate: string) => {
        const date = new Date(isoDate);
        return date.toISOString().slice(0, 16).replace('T', ' ');
    };

    const getTestDateText = () => {
        switch (language) {
            case LANG_CODE.EN_PROFESSIONAL:
                return 'Test Date';
            case LANG_CODE.JA_PROFESSIONAL:
                return 'Test Date';
            case LANG_CODE.JA_YOUTH:
                return '日付';
            case LANG_CODE.JA_KID:
                return 'ひづけ';
        }
    };

    const getTotalScoreText = () => {
        switch (language) {
            case LANG_CODE.EN_PROFESSIONAL:
                return 'Total Score:';
            case LANG_CODE.JA_PROFESSIONAL:
                return 'Total Score:';
            case LANG_CODE.JA_YOUTH:
                return '総合得点:';
            case LANG_CODE.JA_KID:
                return 'とくてん:';
        }
    };

    const getCertificateText = () => {
        switch (language) {
            case LANG_CODE.EN_PROFESSIONAL:
                return 'Certificate: your score is verified as';
            case LANG_CODE.JA_PROFESSIONAL:
                return 'Certificate: your score is verified as';
            case LANG_CODE.JA_YOUTH:
                return 'Certificate: your score is verified as';
            case LANG_CODE.JA_KID:
                return 'しょうめいしょ。あなたのスコアは';
        }
    };

    const getTotalScoreTrendsText = () => {
        switch (language) {
            case LANG_CODE.EN_PROFESSIONAL:
                return 'Total Score Trends';
            case LANG_CODE.JA_PROFESSIONAL:
                return 'Total Score Trends';
            case LANG_CODE.JA_YOUTH:
                return 'スコアのトレンド';
            case LANG_CODE.JA_KID:
                return 'いままでの てすと の けっか';
        }
    };

    const get7lowestScoresText = () => {
        switch (language) {
            case LANG_CODE.EN_PROFESSIONAL:
                return 'Ones w/ 7 Lowest scores!';
            case LANG_CODE.JA_PROFESSIONAL:
                return 'Ones w/ 7 Lowest scores!';
            case LANG_CODE.JA_YOUTH:
                return 'あなたが最も練習すべき7個';
            case LANG_CODE.JA_KID:
                return 'この7こをれんしゅうしよう';
        }
    };

    const ResultPage = () => (
        <Box sx={{ minWidth: 120 }}>
            <Box sx={{ mt: 3, mb: 7, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, }}>
                <Box sx={{ minWidth: 120 }}>
                    <Card elevation={0} sx={{ width: '90%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }} style={{ marginLeft: '25px', padding: '0px' }}>
                        <FormControl fullWidth>
                            <InputLabel variant="standard" htmlFor="uncontrolled-native" sx={{ fontSize: '20px' }}>
                                {getTestDateText()}
                            </InputLabel>
                            <NativeSelect
                                value={selectedDateUuidPair?.end_at}
                                onChange={handleDateSelection} // Capture the selected option
                                inputProps={{
                                    name: 'date',
                                    id: 'uncontrolled-native',
                                }}
                            >
                                {allAvailableUUIDs !== null &&
                                    allAvailableUUIDs.map((item) => (
                                        <option key={item.uuid} value={item.end_at}>
                                            {formatDate(item.end_at)}
                                        </option>
                                    ))}
                            </NativeSelect>
                        </FormControl>
                    </Card>
                    <Typography variant="h6" color="text.primary" sx={{ mt: 3, fontSize: '16px' }} align="center">
                        {getTotalScoreText()} {calculateAverageScore(constructedData)}
                    </Typography>
                    <Card elevation={0} sx={{ maxWidth: 1000 }} style={{ margin: '0px', padding: '0px' }}>
                        <RadarChart outerRadius={90} width={400} height={400} data={constructedData}>
                            <PolarGrid />
                            <PolarAngleAxis
                                dataKey="subject"
                                tick={(props) => {
                                    const { x, y, payload, textAnchor } = props;
                                    const radius = 0; // Adjust this to fine-tune spacing
                                    const angle = props.payload.coordinate; // Get the angle of the tick
                                    const rotation = 20 - angle;

                                    return (
                                        <text
                                            x={x}
                                            y={y}
                                            textAnchor="middle"
                                            fill="#000"
                                            fontSize={12}
                                            transform={`rotate(${rotation}, ${x}, ${y}) translate(0, -${radius})`}
                                        >
                                            {payload.value}
                                        </text>
                                    );
                                }}
                            />
                            <PolarRadiusAxis />
                            <Radar name="Mike" dataKey="A" stroke="#4dabf5" fill="#4dabf5" fillOpacity={0.1} />
                        </RadarChart>
                    </Card>
                    <Typography variant="h5" color="text.primary" sx={{ mt: 0, fontSize: '20px' }} align="center">
                        {getCertificateText()} {calculateAverageScore(constructedData)}<WorkspacePremiumIcon />
                    </Typography>
                </Box>
                <Box sx={{ minWidth: 120, mt: { xs: 12, sm: 0 } }}>
                    <Typography variant="h5" color="text.primary" sx={{ mt: 0, fontSize: '24px' }} align="center">
                        {getTotalScoreTrendsText()}
                    </Typography>
                    <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={350}>
                        <AreaChart
                            width={100}
                            height={300}
                            data={histricalData}
                            margin={{
                                top: 15,
                                right: 50,
                                left: 5,
                                bottom: 5,
                            }}
                            {...{ overflow: 'visible' }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="datetime" angle={-90} textAnchor='end' />
                            <YAxis />
                            <Tooltip />
                            <Area type="monotone" dataKey="score" stroke="#4dabf5" fill="#4dabf5" fillOpacity={0.3} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
                <Box sx={{ minWidth: 120, mt: { xs: 12, sm: 0 }, mb: { xs: 6, sm: 0 } }}>
                    <Typography variant="h5" color="text.primary" sx={{ mt: 0, fontSize: '24px' }} align="center">
                        {get7lowestScoresText()}
                    </Typography>
                    <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={350}>
                        <BarChart
                            width={100}
                            height={300}
                            data={[...worstSubjectsChartData].reverse()}
                            margin={{
                                top: 15,
                                right: 50,
                                left: 5,
                                bottom: 5,
                            }}
                            {...{ overflow: 'visible' }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" angle={-90} textAnchor='end' />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="score" fill="#4dabf5" fillOpacity={0.3} activeBar={<Rectangle fill="#4dabf5" stroke="blue" />} />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Box>

            <Divider />
            <OnsoAnalysis
                onsoScoreData={onsoScoreData}
            />

        </Box >
    );

    return (
        <ResultPage />
    );
};

export default ExamResult;
